/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file voipIdentitySocket.js
 * @description
 * Tiny Socket.IO client for the VoIP caller-identification events the backend
 * pushes to the agent's `user:<id>` room (`voip:incoming`,
 * `voip:caller-identified`). Clones {@link module:helpers/TeamChatSocket}
 * (Bearer auth, `join:user` on every reconnect). Payloads are accepted bare or
 * in the realtime envelope `{event, payload, emittedAt}`.
 */

import {io as createSocketClient} from 'socket.io-client';
import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken} from '../helpers/storage';

/** Caller-identity events delivered to `user:<id>`. */
export const VOIP_IDENTITY_EVENTS = ['voip:incoming', 'voip:caller-identified'];

/**
 * Unwrap the realtime envelope when present, else return the bare payload.
 * @param {*} data Raw socket payload.
 * @returns {Object} Event payload (always an object).
 */
export const unwrapVoipPayload = (data) => {
    if (data && typeof data === 'object' && data.payload && typeof data.payload === 'object'
        && ('event' in data || 'emittedAt' in data)) {
        return data.payload;
    }

    return data && typeof data === 'object' ? data : {};
};

/**
 * Open the VoIP identity socket.
 * @param {{userId: string, onEvent: (eventName: string, payload: Object) => void}} params
 * @returns {{close: () => void}}
 */
export function connectVoipIdentitySocket({userId, onEvent}) {
    let socket = null;
    let closed = false;

    (async () => {
        let token = null;
        try {
            token = await getAccessToken();
        } catch (_) {
            token = null;
        }
        if (closed || !userId) {
            return;
        }
        try {
            socket = createSocketClient(ApiConfig.apiURL, {
                transports: ['websocket', 'polling'],
                timeout: 5000,
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 3000,
                auth: token ? {token, userId} : {userId},
            });
            socket.on('connect', () => {
                if (!closed) {
                    socket.emit('join:user', userId);
                }
            });
            VOIP_IDENTITY_EVENTS.forEach((eventName) => {
                socket.on(eventName, (data) => {
                    if (closed) {
                        return;
                    }
                    try {
                        onEvent?.(eventName, unwrapVoipPayload(data));
                    } catch (_) {
                        // Best-effort; a handler bug must not kill the socket.
                    }
                });
            });
        } catch (_) {
            socket = null;
        }
    })();

    return {
        close() {
            closed = true;
            if (socket) {
                try {
                    socket.removeAllListeners();
                    socket.disconnect();
                } catch (_) {
                    // ignore teardown races
                }
            }
            socket = null;
        },
    };
}

export default connectVoipIdentitySocket;
